import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Toolbar,
  Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const PassReport = () => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [error, setError] = useState('');

  // Only Admin can access this page
  const isAuthorized = user && user.role === 'Admin';

  if (!isAuthorized) {
    return (
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu'
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు'
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Fetch available years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await api.get('/api/result/years');
        const data = await res.json();
        if (res.ok && data.success) {
          setYears(data.data);
          // Set first year as default
          if (data.data.length > 0) {
            setSelectedYear(data.data[0].value);
          }
        }
      } catch (err) {
        console.error('Error fetching years:', err);
        setError('Failed to fetch years');
      }
    };
    fetchYears();
  }, []);

  // Fetch pass report data when year changes
  useEffect(() => {
    if (selectedYear) {
      fetchPassReportData();
    }
  }, [selectedYear]);

  const fetchPassReportData = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/api/result/pass-report-data?year_id=${selectedYear}`);
      const data = await res.json();

      if (res.ok && data.success) {
        setReportData(data.data);
      } else {
        setError(data.error || 'Failed to fetch report data');
      }
    } catch (err) {
      console.error('Error fetching pass report:', err);
      setError('Failed to fetch pass report data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!reportData) return;

    try {
      setPdfGenerating(true);
      setError('');
      const element = document.getElementById('pass-report-content');
      if (!element) {
        setError('Report content not found');
        setPdfGenerating(false);
        return;
      }

      // Wait for all fonts to be loaded before PDF generation
      await document.fonts.ready;
      console.log('[PDF] All fonts loaded');

      // Add inline styles to ensure proper Telugu rendering in the DOM before capturing
      const inlineStyles = document.createElement('style');
      inlineStyles.id = 'pdf-render-style';
      inlineStyles.textContent = `
        @font-face {
          font-family: 'Noto Sans Telugu';
          font-display: swap;
          src: url('https://fonts.gstatic.com/s/notosanstelugu/v28/WwkwxP2xYV48PV0-4C8QrPESZi8tqQ.0.woff2') format('woff2');
        }
        
        #pass-report-content {
          font-family: 'Noto Sans Telugu', 'Roboto', serif !important;
        }
        
        #pass-report-content * {
          font-family: 'Noto Sans Telugu', 'Roboto', serif !important;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility !important;
          -webkit-text-size-adjust: 100%;
          letter-spacing: 0.02em;
        }
        
        #pass-report-content h1,
        #pass-report-content h2,
        #pass-report-content h3,
        #pass-report-content h4,
        #pass-report-content h5,
        #pass-report-content h6 {
          text-rendering: geometricPrecision !important;
          font-kerning: auto !important;
        }
        
        #pass-report-content table {
          border-collapse: collapse;
        }
        
        #pass-report-content th,
        #pass-report-content td {
          text-rendering: optimizeLegibility !important;
          font-family: 'Noto Sans Telugu', 'Roboto', serif !important;
        }
      `;
      document.head.appendChild(inlineStyles);

      // Give fonts time to apply
      await new Promise(resolve => setTimeout(resolve, 500));

      // Load required libraries
      const [html2canvas, jsPDF] = await Promise.all([
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js').then(() => window.html2canvas),
        loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js').then(() => window.jspdf.jsPDF)
      ]);

      console.log('[PDF] Libraries loaded, rendering canvas...');

      // Use optimized scale: 2.5 (balanced quality vs file size for Telugu text)
      // This provides excellent Telugu rendering while keeping file size reasonable
      const canvas = await html2canvas(element, {
        scale: 2.5,  // Optimized scale: 2.5x for good quality with smaller file size
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
        ignoreElements: (el) => {
          return false; // Include all elements
        },
        // Options for text rendering
        onclone: (clonedDocument) => {
          // Ensure fonts are applied to cloned document
          const style = clonedDocument.createElement('style');
          style.textContent = `
            @font-face {
              font-family: 'Noto Sans Telugu';
              src: url('https://fonts.gstatic.com/s/notosanstelugu/v28/WwkwxP2xYV48PV0-4C8QrPESZi8tqQ.0.woff2') format('woff2');
            }
            * {
              font-family: 'Noto Sans Telugu', 'Roboto', serif !important;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              text-rendering: optimizeLegibility !important;
              font-feature-settings: "kern" 1 !important;
            }
          `;
          clonedDocument.head.appendChild(style);
        }
      });

      console.log('[PDF] Canvas generated:', canvas.width, 'x', canvas.height);

      // Create PDF
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const padding = 10; // mm
      const contentWidth = pageWidth - (2 * padding);
      const contentHeight = pageHeight - (2 * padding);

      // Calculate dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = padding;

      // Create PDF with compression enabled
      const pdf = new jsPDF('p', 'mm', 'a4', true);
      
      // Set PDF compression
      pdf.setProperties({
        title: `PassReport_${selectedYear}`,
        compress: true
      });

      // Convert canvas to JPEG (much smaller than PNG) with 85% quality
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      
      // First page
      pdf.addImage(imgData, 'JPEG', padding, position, imgWidth, imgHeight);
      heightLeft -= contentHeight;
      position = padding;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + padding;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', padding, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      // Save PDF
      pdf.save(`PassReport_${selectedYear}.pdf`);
      console.log('[PDF] PDF saved successfully');
      
      // Cleanup
      const styleEl = document.getElementById('pdf-render-style');
      if (styleEl) {
        document.head.removeChild(styleEl);
      }
      
      setPdfGenerating(false);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF: ' + err.message);
      
      // Cleanup on error
      const styleEl = document.getElementById('pdf-render-style');
      if (styleEl) {
        document.head.removeChild(styleEl);
      }
      
      setPdfGenerating(false);
    }
  };

  // Helper function to load external scripts
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (window.html2canvas && src.includes('html2canvas')) {
        resolve();
        return;
      }
      if (window.jspdf && src.includes('jspdf')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const getYearName = () => {
    const year = years.find(y => y.value === selectedYear);
    return year ? year.name : selectedYear;
  };

  const getTeluguLabel = (key) => {
    const labels = {
      totalRegistrations: 'మొత్తం దరఖాస్తు చేసుకున్న విద్యార్థులు',
      vatuWhoGaveExam: 'పరీక్ష ఇచ్చిన విద్యార్థులు',
      gradingStats: 'గ్రేడింగ్ గణాంకాలు'
    };
    return labels[key] || key;
  };

  const getHindiLabel = (key) => {
    const labels = {
      totalRegistrations: 'कुल पंजीकरण',
      vatuWhoGaveExam: 'परीक्षा देने वाले छात्राएँ',
      gradingStats: 'ग्रेडिंग सांख्यिकी'
    };
    return labels[key] || key;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          {lang === 'telugu' ? 'పరీక్ష ఉత్తీర్ణత నివేదిక' : 'परीक्षा उत्तीर्णता निवेदिका'}
        </Typography>

        {/* Year Selector */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>
              {lang === 'telugu' ? 'సంవత్సరం' : 'वर्ष'}
            </InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              label={lang === 'telugu' ? 'సంవత్సరం' : 'वर्ष'}
            >
              {years.map((year) => (
                <MenuItem key={year.value} value={year.value}>
                  {year.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            startIcon={pdfGenerating ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadPDF}
            disabled={!reportData || loading || pdfGenerating}
          >
            {pdfGenerating 
              ? (lang === 'telugu' ? 'PDF తৈయారు చేస్తున్నది...' : 'PDF तैयार कर रहा है...')
              : (lang === 'telugu' ? 'PDF డౌన్‌లోడ్ చేయి' : 'PDF डाउनलोड करें')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : reportData ? (
        <div id="pass-report-content">
          {/* Report Header */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h5" align="center" sx={{ fontWeight: 'bold', mb: 1 }}>
              {lang === 'telugu' ? 'పరీక్ష ఉత్తీర్ణత నివేదిక' : 'परीक्षा उत्तीर्णता निवेदिका'}
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary">
              {lang === 'telugu' ? 'సంవత్సరం: ' : 'वर्ष: '} {getYearName()}
            </Typography>
          </Paper>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    {lang === 'telugu' ? 'మొత్తం నమోదులు' : 'कुल पंजीकरण'}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {reportData.totalRegistrations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    {lang === 'telugu' ? 'పరీక్ష ఇచ్చిన విద్యార్థులు' : 'परीक्षा देने वाले छात्राएँ'}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#388e3c' }}>
                    {reportData.vatuWhoGaveExam}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>


          {/* Grading Statistics Table */}
          {/* <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              {lang === 'telugu' ? 'గ్రేడింగ్ గణాంకాలు' : 'ग्रेडिंग सांख्यिकी'}
            </Typography>

            {Object.keys(reportData.gradingStats).length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {lang === 'telugu' ? 'గ్రేడ్' : 'ग्रेड'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {lang === 'telugu' ? 'సంఖ్య' : 'संख्या'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {lang === 'telugu' ? 'శాతం' : 'प्रतिशत'}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(reportData.gradingStats).map(([grade, count]) => {
                      const percentage = reportData.vatuWhoGaveExam > 0
                        ? ((count / reportData.vatuWhoGaveExam) * 100).toFixed(2)
                        : 0;
                      return (
                        <TableRow key={grade}>
                          <TableCell>{grade}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                          <TableCell align="right">{percentage}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                {lang === 'telugu' ? 'ఎటువంటి గ్రేడింగ్ డేటా లేదు' : 'कोई ग्रेडिंग डेटा नहीं'}
              </Typography>
            )}
          </Paper> */}

          {/* Pariksha Type-wise Attendance Section */}
          {reportData.pariksha_type_wise_attendance && Object.keys(reportData.pariksha_type_wise_attendance).length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#1976d2' }}>
                {lang === 'telugu' ? 'పరీక్ష రకానికి సంబంధించిన హాజరు' : 'परीक्षा प्रकार अनुसार उपस्थिति'}
              </Typography>

              {Object.entries(reportData.pariksha_type_wise_attendance).map(([parikshaType, typeData]) => {
                // Translation map for pariksha_type codes to Telugu names
                const parikshaTypeTranslations = {
                  'krishna_yajurveda': 'కృష్ణ యజుర్వేదం',
                  'sanskrit': 'సంస్కృతం',
                  'vedadhyayi_sahityam': 'వేదాధ్యాయి సాహిత్యం',
                  'athyuthama_pariksha': 'అత్యుత్తమ పరీక్ష'
                };
                const displayType = parikshaTypeTranslations[parikshaType] || parikshaType;
                
                return (
                  <Paper key={parikshaType} elevation={1} sx={{ p: 3, mb: 3 }}>
                    {/* Pariksha Type Header */}
                    <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid #1976d2' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {displayType}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {lang === 'telugu' ? 'మొత్తం విద్యార్థులు' : 'कुल छात्र'}: {typeData.count}
                      </Typography>
                    </Box>

                    {/* Group by Pathasala */}
                    <Stack spacing={2}>
                      {Object.entries(typeData.patashalas).map(([pathasalaName, vatus]) => (
                        <Box key={pathasalaName} sx={{ pl: 2, borderLeft: '3px solid #388e3c' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#388e3c', mb: 1.5 }}>
                            {pathasalaName}
                          </Typography>
                          
                          {/* 2-Column Table Layout for Vatus */}
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: 2,
                              pl: 2
                            }}
                          >
                            {vatus.map((vatu, idx) => (
                              <Box
                                key={`${vatu.vatu_id}-${idx}`}
                                sx={{
                                  p: 1,
                                  backgroundColor: idx % 2 === 0 ? '#f5f5f5' : '#ffffff',
                                  borderRadius: '4px',
                                  borderLeft: '3px solid #8bc34a',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Typography variant="body2">
                                  {vatu.vatu_name}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          )}

          {/* Summary Statistics */}
          <Paper elevation={1} sx={{ p: 3, mt: 4, backgroundColor: '#fafafa' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#1976d2' }}>
              {lang === 'telugu' ? 'మొత్తం గణాంకాలు' : 'कुल सांख्यिकी'}
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                <Typography>
                  {lang === 'telugu' ? 'మొత్తం దరఖాస్తు చేసుకున్న విద్యార్థులు :' : 'कुल पंजीकरण:'}
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {reportData.totalRegistrations}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
                <Typography>
                  {lang === 'telugu' ? 'పరీక్ష ఇచ్చిన విద్యార్థులు :' : 'परीक्षा देने वाली छात्राएँ :'}
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {reportData.vatuWhoGaveExam}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography>
                  {lang === 'telugu' ? 'భాగస్వామ్యం:' : 'भागीदारी:'}
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {reportData.totalRegistrations > 0
                    ? ((reportData.vatuWhoGaveExam / reportData.totalRegistrations) * 100).toFixed(2)
                    : 0}
                  %
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </div>
      ) : (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {lang === 'telugu' ? 'డేటా లోడ్ కాలేదు' : 'डेटा लोड नहीं हो सका'}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default PassReport;
